import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { CONTACT, NAV_LINKS } from "@/data/services";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetClose } from "@/components/ui/sheet";

export const Logo = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
    <div className="w-8 h-8 rounded bg-[#3D8B4D] flex items-center justify-center text-white font-black text-base shadow-inner font-display">
      Z
    </div>
    <div>
      <span className="font-black text-lg tracking-wider text-white font-display">ZWILLINGSWERK</span>
      <span className="hidden sm:block text-[11px] text-[#A3D9A5] font-medium border-l border-[#1E5631] pl-2 ml-0 sm:ml-2 sm:inline">
        Garten- &amp; Hausmeisterservice Herten
      </span>
    </div>
  </Link>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50">
      {/* Dunkle Kontakt-Leiste */}
      <div className="bg-[#122B18] text-[#F2F7F2] border-b border-[#1E5631] shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
            <span className="hidden md:flex items-center gap-1.5 text-[#D7E5D9]" data-testid="header-hours">
              <span className="inline-block w-2 h-2 rounded-full bg-[#4ADE80]" />
              <span>{CONTACT.hours}</span>
            </span>
            <a
              href={CONTACT.phoneHref}
              data-testid="header-phone-button"
              className="inline-flex items-center gap-2 bg-[#3D8B4D] hover:bg-[#4AA05C] text-white font-bold px-4 py-2 rounded-md shadow-sm transition-colors text-xs sm:text-sm"
            >
              <Phone className="w-4 h-4" aria-hidden="true" />
              <span>{CONTACT.phoneDisplay}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Navigationsleiste */}
      <nav className="bg-white border-b border-[#D7E5D9]" aria-label="Hauptnavigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="hidden lg:flex items-center space-x-6 text-sm font-semibold whitespace-nowrap text-[#1B281B]">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `transition-colors hover:text-[#1E5631] ${isActive ? "text-[#1E5631] border-b-2 border-[#1E5631] pb-0.5" : ""}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
          <div className="hidden lg:block">
            <span className="text-xs font-bold text-[#1E5631] border border-[#1E5631] px-3 py-1.5 rounded" data-testid="nav-radius-badge">
              Anfahrt 20 km inklusive
            </span>
          </div>

          {/* Mobile Navigation */}
          <div className="flex lg:hidden items-center justify-between w-full">
            <span className="text-xs font-bold text-[#1E5631]">Festpreise · 20 km Anfahrt frei</span>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-button"
                  aria-label="Menü öffnen"
                  className="inline-flex items-center gap-2 text-[#1E5631] font-bold text-sm border border-[#D7E5D9] rounded-md px-3 py-2"
                >
                  <Menu className="w-5 h-5" />
                  Menü
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-white w-4/5 sm:max-w-sm" data-testid="mobile-menu">
                <SheetTitle className="font-display text-[#122B18] text-left">ZWILLINGSWERK</SheetTitle>
                <nav className="mt-6 flex flex-col gap-1" aria-label="Mobile Navigation">
                  {NAV_LINKS.map((l) => (
                    <SheetClose asChild key={l.to}>
                      <NavLink
                        to={l.to}
                        data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) =>
                          `px-3 py-3 rounded-lg font-semibold text-sm transition-colors ${
                            isActive ? "bg-[#F2F7F2] text-[#1E5631]" : "text-[#1B281B] hover:bg-[#F8FAF8]"
                          }`
                        }
                      >
                        {l.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                </nav>
                <div className="mt-6 pt-6 border-t border-[#E6EFE7] space-y-3">
                  <a
                    href={CONTACT.phoneHref}
                    data-testid="mobile-menu-call-button"
                    className="flex items-center justify-center gap-2 bg-[#1E5631] text-white font-bold py-3 rounded-lg text-sm"
                  >
                    <Phone className="w-4 h-4" /> {CONTACT.phoneDisplay}
                  </a>
                  <a
                    href={CONTACT.emailHref}
                    data-testid="mobile-menu-mail-button"
                    className="flex items-center justify-center gap-2 border-2 border-[#1E5631] text-[#1E5631] font-bold py-3 rounded-lg text-sm"
                  >
                    E-Mail schreiben
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
}
